import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import {
  fetchPriceHistory,
  fetchPriceMoves,
  type PriceHistoryPointDto,
  type PriceMoveDirection,
  type PriceMoveDto,
} from '../api/marketAnalytics'
import {
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/myPrices'
import { displaySupplierName, shortenSupplierName } from '../utils/supplierName'

type AnalyticsScreenProps = {
  organizationId: number
  hasPremium: boolean
  onOpenProfile?: () => void
}

type SortMode = 'magnitude' | 'date'
type MagDirection = 'desc' | 'asc'
type DateDirection = 'desc' | 'asc'

const PREMIUM_URL = 'https://market.provisium.ru'
const UP_COLLAPSED_KEY = 'provisium.miniapp.analytics.upCollapsed'
const DOWN_COLLAPSED_KEY = 'provisium.miniapp.analytics.downCollapsed'
/** First paint: smaller page so UI unlocks before the full 50-move query. */
const MOVES_FIRST_CHUNK = 12
const MOVES_FULL_LIMIT = 50

const readCollapsed = (key: string): boolean => localStorage.getItem(key) === 'true'

const writeCollapsed = (key: string, collapsed: boolean) => {
  localStorage.setItem(key, collapsed ? 'true' : 'false')
}

const parseObservedTs = (value: string | null | undefined): number => {
  if (!value) {
    return 0
  }
  const t = Date.parse(value)
  return Number.isNaN(t) ? 0 : t
}

const moveNameKey = (move: PriceMoveDto): string =>
  (move.item_name?.trim() || move.supplier_name?.trim() || '').toLocaleLowerCase('ru')

const formatPrice = (price: number): string =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(price)

const formatDeltaPct = (pct: number | null): string => {
  if (pct == null || !Number.isFinite(pct)) {
    return '—'
  }
  const rounded = Math.round(Math.abs(pct) * 10) / 10
  return `${rounded}%`
}

const formatDaysAgo = (iso: string): string => {
  const observed = new Date(iso)
  if (Number.isNaN(observed.getTime())) {
    return '—'
  }
  const now = new Date()
  const diffMs = now.getTime() - observed.getTime()
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (days <= 0) {
    return 'сегодня'
  }
  if (days === 1) {
    return 'вчера'
  }
  return `${days} дн назад`
}

/** Absolute stamp for date-sort mode — bulk imports share one day, so relative labels alone look identical. */
const formatObservedStamp = (iso: string): string => {
  const observed = new Date(iso)
  if (Number.isNaN(observed.getTime())) {
    return '—'
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(observed)
}

const moveMagnitude = (move: PriceMoveDto): number =>
  Math.abs(move.delta_pct ?? move.delta_abs ?? 0)

const moveName = (move: PriceMoveDto): string =>
  move.item_name?.trim() || move.supplier_name?.trim() || `Позиция #${move.catalog_item_id}`

const supplierLabel = (
  move: PriceMoveDto,
  suppliers: RestaurantPrivateSupplierDto[],
): string => {
  if (move.supplier_name?.trim()) {
    return shortenSupplierName(move.supplier_name)
  }
  const match = suppliers.find((supplier) => supplier.id === move.private_supplier_id)
  return displaySupplierName(match?.displayLabel, match?.legalName)
}

const isMoveUp = (move: PriceMoveDto): boolean => move.delta_abs > 0

type SparklineProps = {
  points: PriceHistoryPointDto[]
  width?: number
  height?: number
}

const Sparkline = ({ points, width = 280, height = 44 }: SparklineProps) => {
  const values = points.map((point) => point.price)
  if (values.length < 2) {
    return null
  }

  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const step = width / (values.length - 1)

  const linePoints = values
    .map((value, index) => {
      const x = index * step
      const y = height - ((value - min) / range) * (height - 6) - 3
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const areaPoints = `0,${height} ${linePoints} ${width},${height}`
  const lastY = height - ((values[values.length - 1] - min) / range) * (height - 6) - 3

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      aria-hidden="true"
      className="rp-sparkline"
    >
      <polygon points={areaPoints} fill="var(--accent-soft)" />
      <polyline
        points={linePoints}
        fill="none"
        stroke="var(--echat-outgoing-bg)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={width} cy={lastY} r="3" fill="var(--echat-outgoing-bg)" />
    </svg>
  )
}

type MoveAccordionProps = {
  move: PriceMoveDto
  organizationId: number
  supplierName: string
  showObservedHint?: boolean
}

const MoveAccordion = ({
  move,
  organizationId,
  supplierName,
  showObservedHint = false,
}: MoveAccordionProps) => {
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<PriceHistoryPointDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const up = isMoveUp(move)

  useEffect(() => {
    if (!open || history.length > 0) {
      return
    }

    let cancelled = false
    const loadHistory = async () => {
      setHistoryLoading(true)
      try {
        const points = await fetchPriceHistory(organizationId, move.catalog_item_id, 90)
        if (!cancelled) {
          setHistory(points)
        }
      } catch {
        if (!cancelled) {
          setHistory([])
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false)
        }
      }
    }

    void loadHistory()

    return () => {
      cancelled = true
    }
  }, [open, history.length, move.catalog_item_id, organizationId])

  const handleToggle = () => setOpen((current) => !current)

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleToggle()
    }
  }

  return (
    <article className={`rp-acc${open ? ' rp-acc--open' : ''}`}>
      <button
        type="button"
        className="rp-acc__head"
        aria-expanded={open}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span className="rp-acc__name">{moveName(move)}</span>
        <span className={up ? 'rp-delta rp-delta--up' : 'rp-delta rp-delta--down'}>
          {up ? '▲' : '▼'} {formatDeltaPct(move.delta_pct)}
        </span>
        {showObservedHint ? (
          <span className="rp-acc__when" aria-hidden="true">
            {formatObservedStamp(move.observed_at)}
          </span>
        ) : null}
        <span className="rp-acc__chev" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <div className="rp-acc__body">
          <div className="rp-acc__row">
            <span>Поставщик</span>
            <span>{supplierName}</span>
          </div>
          <div className="rp-acc__row">
            <span>Было → стало</span>
            <span>
              {formatPrice(move.previous_price)} → {formatPrice(move.current_price)}
            </span>
          </div>
          <div className="rp-acc__row">
            <span>Изменилось</span>
            <span>{formatDaysAgo(move.observed_at)}</span>
          </div>
          {historyLoading ? (
            <p className="echat-muted">Загрузка графика…</p>
          ) : history.length > 0 ? (
            <div className="rp-acc__chart">
              <Sparkline points={history} />
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

const handleOpenPremium = () => {
  window.open(PREMIUM_URL, '_blank', 'noopener,noreferrer')
}

export const AnalyticsScreen = ({
  organizationId,
  hasPremium,
  onOpenProfile: _onOpenProfile,
}: AnalyticsScreenProps) => {
  void _onOpenProfile

  const [upCollapsed, setUpCollapsed] = useState(() => readCollapsed(UP_COLLAPSED_KEY))
  const [downCollapsed, setDownCollapsed] = useState(() => readCollapsed(DOWN_COLLAPSED_KEY))
  const [direction, setDirection] = useState<PriceMoveDirection>('all')
  const [allMoves, setAllMoves] = useState<PriceMoveDto[]>([])
  const [supplierId, setSupplierId] = useState<number | null>(null)
  const [sortMode, setSortMode] = useState<SortMode>('magnitude')
  const [magDirection, setMagDirection] = useState<MagDirection>('desc')
  const [dateDirection, setDateDirection] = useState<DateDirection>('desc')
  const [suppliers, setSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadGeneration = useRef(0)

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current
    setLoading(true)
    setLoadingMore(false)
    setError(null)
    setAllMoves([])

    const suppliersPromise = listRestaurantPrivateSuppliers(organizationId).then((list) => {
      if (generation === loadGeneration.current) {
        setSuppliers(list)
      }
      return list
    })

    try {
      const firstChunk = await fetchPriceMoves(organizationId, {
        limit: MOVES_FIRST_CHUNK,
        privateSupplierId: supplierId,
      })
      if (generation !== loadGeneration.current) {
        return
      }
      setAllMoves(firstChunk)
      setLoading(false)

      setLoadingMore(true)
      const [fullList] = await Promise.all([
        fetchPriceMoves(organizationId, {
          limit: MOVES_FULL_LIMIT,
          privateSupplierId: supplierId,
        }),
        suppliersPromise.catch(() => []),
      ])
      if (generation !== loadGeneration.current) {
        return
      }
      setAllMoves(fullList)
    } catch (loadError) {
      if (generation !== loadGeneration.current) {
        return
      }
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить аналитику')
    } finally {
      if (generation === loadGeneration.current) {
        setLoading(false)
        setLoadingMore(false)
      }
      void suppliersPromise.catch(() => undefined)
    }
  }, [organizationId, supplierId])

  const moves = useMemo(() => {
    if (direction === 'all') {
      return allMoves
    }
    if (direction === 'up') {
      return allMoves.filter((move) => isMoveUp(move))
    }
    return allMoves.filter((move) => !isMoveUp(move))
  }, [allMoves, direction])

  useEffect(() => {
    if (!hasPremium) {
      return
    }
    void load()
  }, [hasPremium, load])

  const topUps = useMemo(
    () =>
      [...allMoves]
        .filter((move) => isMoveUp(move))
        .sort((a, b) => moveMagnitude(b) - moveMagnitude(a))
        .slice(0, 3),
    [allMoves],
  )

  const topDowns = useMemo(
    () =>
      [...allMoves]
        .filter((move) => !isMoveUp(move))
        .sort((a, b) => moveMagnitude(b) - moveMagnitude(a))
        .slice(0, 3),
    [allMoves],
  )

  const sortedMoves = useMemo(() => {
    const rows = [...moves]
    if (sortMode === 'date') {
      return rows.sort((a, b) => {
        const ta = parseObservedTs(a.observed_at)
        const tb = parseObservedTs(b.observed_at)
        if (ta !== tb) {
          return dateDirection === 'desc' ? tb - ta : ta - tb
        }
        // Bulk imports often share one observed_at — fall back so order
        // still clearly differs from magnitude sort.
        const byName = moveNameKey(a).localeCompare(moveNameKey(b), 'ru')
        if (byName !== 0) {
          return dateDirection === 'desc' ? byName : -byName
        }
        return dateDirection === 'desc'
          ? b.catalog_item_id - a.catalog_item_id
          : a.catalog_item_id - b.catalog_item_id
      })
    }
    return rows.sort((a, b) => {
      const diff = moveMagnitude(b) - moveMagnitude(a)
      if (diff !== 0) {
        return magDirection === 'desc' ? diff : -diff
      }
      const byObserved = parseObservedTs(b.observed_at) - parseObservedTs(a.observed_at)
      if (byObserved !== 0) {
        return byObserved
      }
      return b.catalog_item_id - a.catalog_item_id
    })
  }, [dateDirection, magDirection, moves, sortMode])

  const handleToggleUp = () => {
    const next = !upCollapsed
    setUpCollapsed(next)
    writeCollapsed(UP_COLLAPSED_KEY, next)
  }

  const handleToggleDown = () => {
    const next = !downCollapsed
    setDownCollapsed(next)
    writeCollapsed(DOWN_COLLAPSED_KEY, next)
  }

  const handleSortMagnitude = () => {
    if (sortMode === 'magnitude') {
      setMagDirection((current) => (current === 'desc' ? 'asc' : 'desc'))
      return
    }
    setSortMode('magnitude')
  }

  const handleSortDate = () => {
    if (sortMode === 'date') {
      setDateDirection((current) => (current === 'desc' ? 'asc' : 'desc'))
      return
    }
    setDateDirection('desc')
    setSortMode('date')
  }

  if (!hasPremium) {
    return (
      <section className="rp-screen" aria-label="Аналитика цен">
        <article className="echat-panel echat-panel--highlight">
          <h2 className="echat-panel__title">Аналитика цен — в Premium</h2>
          <p className="echat-panel__text">
            Отслеживайте, как меняются цены поставщиков, и покупайте выгоднее
          </p>
          <ul className="echat-lines">
            <li>Движения цен в ваших прайсах — рост и падение</li>
            <li>История цены по каждой позиции</li>
            <li>Ценовые коридоры по категориям — где ваша цена относительно рынка</li>
          </ul>
          <button type="button" className="echat-btn echat-btn--primary" onClick={handleOpenPremium}>
            Подключить Premium
          </button>
        </article>
      </section>
    )
  }

  return (
    <section className="rp-screen" aria-label="Аналитика цен">
      <p className="echat-inbox__section-label">
        Топ изменений
        <span className="rp-section-pill">30 дней</span>
      </p>

      <div className="rp-stack">
        <article className={`rp-collapse${upCollapsed ? '' : ' rp-collapse--open'}`}>
          <button type="button" className="rp-collapse__head rp-collapse__head--up" onClick={handleToggleUp}>
            ▲ Подорожали
            <span className="rp-collapse__chev" aria-hidden="true">
              ▾
            </span>
          </button>
          {!upCollapsed ? (
            <div className="rp-collapse__body">
              {loading && topUps.length === 0 ? (
                <div className="rp-skeleton-stack" aria-hidden="true">
                  <div className="rp-skeleton rp-skeleton--line" />
                  <div className="rp-skeleton rp-skeleton--line" />
                  <div className="rp-skeleton rp-skeleton--line" />
                </div>
              ) : topUps.length === 0 ? (
                <p className="echat-muted">—</p>
              ) : (
                <ul className="rp-list rp-list--flat">
                  {topUps.map((move) => (
                    <li key={move.catalog_item_id} className="rp-top-item">
                      <div className="rp-top-item__main">
                        <p className="rp-card__title">{moveName(move)}</p>
                        <p className="rp-card__meta">{supplierLabel(move, suppliers)}</p>
                      </div>
                      <p className="rp-delta rp-delta--up">+{formatDeltaPct(move.delta_pct)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </article>

        <article className={`rp-collapse${downCollapsed ? '' : ' rp-collapse--open'}`}>
          <button
            type="button"
            className="rp-collapse__head rp-collapse__head--down"
            onClick={handleToggleDown}
          >
            ▼ Подешевели
            <span className="rp-collapse__chev" aria-hidden="true">
              ▾
            </span>
          </button>
          {!downCollapsed ? (
            <div className="rp-collapse__body">
              {loading && topDowns.length === 0 ? (
                <div className="rp-skeleton-stack" aria-hidden="true">
                  <div className="rp-skeleton rp-skeleton--line" />
                  <div className="rp-skeleton rp-skeleton--line" />
                  <div className="rp-skeleton rp-skeleton--line" />
                </div>
              ) : topDowns.length === 0 ? (
                <p className="echat-muted">—</p>
              ) : (
                <ul className="rp-list rp-list--flat">
                  {topDowns.map((move) => (
                    <li key={move.catalog_item_id} className="rp-top-item">
                      <div className="rp-top-item__main">
                        <p className="rp-card__title">{moveName(move)}</p>
                        <p className="rp-card__meta">{supplierLabel(move, suppliers)}</p>
                      </div>
                      <p className="rp-delta rp-delta--down">−{formatDeltaPct(move.delta_pct)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </article>
      </div>

      <p className="echat-inbox__section-label">Все движения</p>

      <div className="rp-toolbar rp-toolbar--stack">
        <div className="rp-seg" role="tablist" aria-label="Направление">
          {(
            [
              { id: 'all' as const, label: 'Все' },
              { id: 'up' as const, label: 'Подорожали' },
              { id: 'down' as const, label: 'Подешевели' },
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={direction === item.id}
              className={direction === item.id ? 'rp-seg__btn rp-seg__btn--active' : 'rp-seg__btn'}
              onClick={() => setDirection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="rp-seg" role="tablist" aria-label="Сортировка">
          <button
            type="button"
            role="tab"
            aria-selected={sortMode === 'magnitude'}
            className={
              sortMode === 'magnitude' ? 'rp-seg__btn rp-seg__btn--active' : 'rp-seg__btn'
            }
            onClick={handleSortMagnitude}
          >
            {magDirection === 'desc' ? 'По цене ↓' : 'По цене ↑'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sortMode === 'date'}
            className={sortMode === 'date' ? 'rp-seg__btn rp-seg__btn--active' : 'rp-seg__btn'}
            onClick={handleSortDate}
          >
            {dateDirection === 'desc' ? 'По дате ↓' : 'По дате ↑'}
          </button>
        </div>

        <div className="rp-chips" role="group" aria-label="Поставщики">
          <button
            type="button"
            className={supplierId == null ? 'echat-chip echat-chip--selected' : 'echat-chip'}
            onClick={() => setSupplierId(null)}
          >
            Все поставщики
          </button>
          {suppliers.map((supplier) => (
            <button
              key={supplier.id}
              type="button"
              className={
                supplierId === supplier.id ? 'echat-chip echat-chip--selected' : 'echat-chip'
              }
              onClick={() => setSupplierId(supplier.id)}
            >
              {displaySupplierName(supplier.displayLabel, supplier.legalName)}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading && sortedMoves.length === 0 ? (
        <div className="rp-stack" aria-busy="true" aria-label="Загрузка движений">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="rp-skeleton rp-skeleton--acc" />
          ))}
        </div>
      ) : null}

      {!loading && sortedMoves.length === 0 ? (
        <div className="echat-inbox__empty">
          <p className="echat-inbox__empty-title">Нет изменений</p>
          <p className="echat-muted">По выбранным фильтрам движений цен пока нет</p>
        </div>
      ) : null}

      <div className="rp-stack">
        {sortedMoves.map((move, index) => (
          <MoveAccordion
            key={`${sortMode}-${dateDirection}-${move.catalog_item_id}-${move.observed_at}-${index}`}
            move={move}
            organizationId={organizationId}
            supplierName={supplierLabel(move, suppliers)}
            showObservedHint={sortMode === 'date'}
          />
        ))}
        {loadingMore ? (
          <p className="echat-muted rp-loading-more" role="status">
            Подгружаем остальные изменения…
          </p>
        ) : null}
      </div>
    </section>
  )
}
